import { Button, Spinner } from "@bphxd/ds-core-react";
import React, { useState } from "react";

interface ACUploadModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
  isLoading: boolean;
}

export default function ACUploadModal({ show, onClose, onSubmit, isLoading }: ACUploadModalProps) {
  const [formData, setFormData] = useState({
    soldToName: "",
    soldToNumber: "",
    customerEmail: "",
    certificationAvailable: "YES",
    expiryDate: "",
    startDate: "",
    certificateFile: null as File | null,
    certificateFileBase64: "",
    certificateFileName: "",
    certificateFileType: ""
  });

  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };
const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB in bytes

const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.files && e.target.files.length > 0) {
    const file = e.target.files[0];
    const ALLOWED_FILE_TYPE = "application/pdf";
    
    // Validate file type
    if (file.type !== ALLOWED_FILE_TYPE) {
      setErrors(prev => ({
        ...prev,
        certificateFile: "Only PDF files are allowed"
      }));
      setFileName("");
      setFormData(prev => ({
        ...prev,
        certificateFile: null,
        certificateFileBase64: "",
        certificateFileName: "",
        certificateFileType: ""
      }));
      return;
    }
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({
        ...prev,
        certificateFile: `File size must not exceed ${MAX_FILE_SIZE / (1024 * 1024)}MB`
      }));
      setFileName("");
      setFormData(prev => ({
        ...prev,
        certificateFile: null,
        certificateFileBase64: "",
        certificateFileName: "",
        certificateFileType: ""
      }));
      return;
    }
    
    setFileName(file.name);
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result as string;
      setFormData(prev => ({
        ...prev,
        certificateFile: file,
        certificateFileBase64: base64String.split(',')[1],
        certificateFileName: file.name,
        certificateFileType: file.type
      }));
    };
    reader.readAsDataURL(file);
    
    // Clear file error when valid file is selected
    if (errors.certificateFile) {
      setErrors(prev => ({
        ...prev,
        certificateFile: ""
      }));
    }
  }
};
  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files && e.target.files.length > 0) {
  //     const file = e.target.files[0];
  //     setFileName(file.name);
      
  //     // Convert file to base64
  //     const reader = new FileReader();
  //     reader.onload = (event) => {
  //       const base64String = event.target?.result as string;
  //       setFormData(prev => ({
  //         ...prev,
  //         certificateFile: file,
  //         certificateFileBase64: base64String.split(',')[1], // Remove data:application/pdf;base64, prefix
  //         certificateFileName: file.name,
  //         certificateFileType: file.type
  //       }));
  //     };
  //     reader.readAsDataURL(file);
      
  //     // Clear file error when file is selected
  //     if (errors.certificateFile) {
  //       setErrors(prev => ({
  //         ...prev,
  //         certificateFile: ""
  //       }));
  //     }
  //   }
  // };
  const getTodayDate = () => {
    const today = new Date();
    today.setMonth(today.getMonth() + 3);
    return today.toISOString().split('T')[0];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.soldToName.trim()) {
      newErrors.soldToName = "SOLD_TO Name is required";
    }
    if (!formData.soldToNumber.trim()) {
      newErrors.soldToNumber = "SOLD_TO Number is required";
    }
    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = "Customer Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = "Please enter a valid email address";
    }
    // if (formData.certificationAvailable !== "YES") {
    //   newErrors.certificationAvailable = "You must select YES for Annual Certification";
    // }
    if (!formData.expiryDate) {
      newErrors.expiryDate = "Certificate Expiry Date is required";
    }
    if (!formData.startDate) {
      newErrors.startDate = "Certificate Start Date is required";
    }
    if (!formData.certificateFile) {
      newErrors.certificateFile = "Certificate File is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Prepare data to send (exclude certificateFile object, keep base64 version)
      const submitData = {
        soldToName: formData.soldToName,
        soldToNumber: formData.soldToNumber,
        customerEmail: formData.customerEmail,
        certificationAvailable: formData.certificationAvailable,
        expiryDate: formData.expiryDate,
        startDate: formData.startDate,
        certificateFileBase64: formData.certificateFileBase64,
        certificateFileName: formData.certificateFileName,
        certificateFileType: formData.certificateFileType
      };
      
      onSubmit(submitData);
      resetForm();
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      soldToName: "",
      soldToNumber: "",
      customerEmail: "",
      certificationAvailable: "YES",
      expiryDate: "",
      startDate: "",
      certificateFile: null,
      certificateFileBase64: "",
      certificateFileName: "",
      certificateFileType: ""
    });
    setFileName("");
    setErrors({});
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: "transparent" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Upload Annual Certificate</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleCancel}
              disabled={isLoading}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="soldToName" className="form-label">Sold To Name (Customer Full Name): <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.soldToName ? 'is-invalid' : ''}`}
                  placeholder="Enter Sold To Name (Customer Full Name)"
                  id="soldToName"
                  name="soldToName"
                  value={formData.soldToName}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.soldToName && <div className="invalid-feedback d-block">{errors.soldToName}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="soldToNumber" className="form-label">Sold To Id(Customer Code): <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.soldToNumber ? 'is-invalid' : ''}`}
                  placeholder="Enter Sold To Id(Customer Code)"
                  id="soldToNumber"
                  name="soldToNumber"
                  value={formData.soldToNumber}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.soldToNumber && <div className="invalid-feedback d-block">{errors.soldToNumber}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="customerEmail" className="form-label">Customer Email: <span className="text-danger">*</span></label>
                <input
                  type="email"
                  className={`form-control ${errors.customerEmail ? 'is-invalid' : ''}`}
                  placeholder="Enter Customer Email"
                  id="customerEmail"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.customerEmail && <div className="invalid-feedback d-block">{errors.customerEmail}</div>}
              </div>
              {/* <div className="mb-3">
                <label htmlFor="certificationAvailable" className="form-label">Annual Certification Available: <span className="text-danger">*</span></label>
                <select
                  className={`form-select ${errors.certificationAvailable ? 'is-invalid' : ''}`}
                  id="certificationAvailable"
                  name="certificationAvailable"
                  value={formData.certificationAvailable}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
                {errors.certificationAvailable && <div className="invalid-feedback d-block">{errors.certificationAvailable}</div>}
              </div> */}
              <div className="mb-3">
                <label htmlFor="startDate" className="form-label">Certificate Start Date: <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.startDate && <div className="invalid-feedback d-block">{errors.startDate}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="expiryDate" className="form-label">Certificate Expiry Date: <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-control ${errors.expiryDate ? 'is-invalid' : ''}`}
                  id="expiryDate"
                  name="expiryDate"
                  min={getTodayDate()}
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.expiryDate && <div className="invalid-feedback d-block">{errors.expiryDate}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="certificateFile" className="form-label">Upload Certificate File: <span className="text-danger">*</span></label>
                <input
                  type="file"
                  className={`form-control ${errors.certificateFile ? 'is-invalid' : ''}`}
                  id="certificateFile"
                  name="certificateFile"
                  onChange={handleFileChange}
                  accept=".pdf"
                  disabled={isLoading}
                />
                {errors.certificateFile && <div className="invalid-feedback d-block">{errors.certificateFile}</div>}
                {fileName && (
                  <small className="text-muted d-block mt-2">Selected file: {fileName}</small>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <Button
                rounded="pill"
                level="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                rounded="pill"
                theme="brand"
                level="primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" color="light" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}